// src/components/admin/ContactInfoEditor.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, getDocs, collection, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { runLocationMigration } from '../../utils/locationMigration';
import '../../styles/contact.css';

// Generates a stable, slug-based locationId from a human name.
// "Action Paragon" → "loc_action_paragon"
// Only called for new (unsaved) locations — existing IDs are never changed.
function generateLocationId(name) {
  if (!name || !name.trim()) return '';
  return (
    'loc_' +
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 20)
  );
}

function ContactInfoEditor({ contact, onUpdate, salonId, onPreview }) {
  // Org-level fields (email + social media — same at all locations)
  const [orgEmail, setOrgEmail]     = useState('');
  const [socialMedia, setSocialMedia] = useState({ facebook: '', instagram: '', twitter: '' });

  // Per-location cards
  const [locations, setLocations]       = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);

  // true until the first successful save that includes a locations array
  const [needsMigration, setNeedsMigration] = useState(false);

  // UI state
  const [saving, setSaving]           = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [saveError, setSaveError]     = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(null); // index of card pending removal
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Initialise org-level fields from props ──────────────────────────────────
  useEffect(() => {
    setOrgEmail(contact?.email || '');
    setSocialMedia({
      facebook:  contact?.socialMedia?.facebook  || '',
      instagram: contact?.socialMedia?.instagram || '',
      twitter:   contact?.socialMedia?.twitter   || '',
    });
  }, [contact]);

  // ── Load locations from Firestore ───────────────────────────────────────────
  useEffect(() => {
    if (!salonId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const salonSnap = await getDoc(doc(db, 'salons', salonId));
        if (cancelled) return;

        if (salonSnap.exists()) {
          const data = salonSnap.data();

          if (data.locations && data.locations.length > 0) {
            // Attach a stable _key so the card's React key never changes
            setLocations(data.locations.map((loc, i) => ({ ...loc, _key: loc.locationId || `existing_${i}` })));
            setNeedsMigration(data.locationsMigrated !== true);
          } else {
            setLocations([
              {
                locationId:   '',
                locationName: '',
                address:      data.address || '',
                phone:        data.phone   || '',
                isHeadOffice: true,
                _isNew:       true,
                _key:         'new_0',
              },
            ]);
            setNeedsMigration(true);
          }
        }
      } catch (err) {
        console.error('Error loading locations:', err);
      } finally {
        if (!cancelled) setLocationsLoaded(true);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [salonId]);

  // ── Location card helpers ───────────────────────────────────────────────────
  const updateLocationField = (index, field, value) => {
    setLocations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-generate locationId only while the card is still unsaved
      if (field === 'locationName' && updated[index]._isNew) {
        updated[index].locationId = generateLocationId(value);
      }
      return updated;
    });
    clearFieldError(`loc_${index}_${field}`);
  };

  const setHeadOffice = (index) => {
    setLocations(prev => prev.map((loc, i) => ({ ...loc, isHeadOffice: i === index })));
  };

  const addLocation = () => {
    setLocations(prev => [
      ...prev,
      {
        locationId:   '',
        locationName: '',
        address:      '',
        phone:        '',
        isHeadOffice: false,
        _isNew:       true,
        _key:         `new_${Date.now()}`,
      },
    ]);
  };

  const requestRemove = (index) => setRemoveConfirm(index);
  const cancelRemove  = ()      => setRemoveConfirm(null);

  const confirmRemove = () => {
    const index = removeConfirm;
    setRemoveConfirm(null);
    setLocations(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      // If the removed card was Head Office, promote the first remaining card
      if (!filtered.some(l => l.isHeadOffice) && filtered.length > 0) {
        filtered[0] = { ...filtered[0], isHeadOffice: true };
      }
      return filtered;
    });
  };

  // ── Field error helpers ─────────────────────────────────────────────────────
  const clearFieldError = (key) => {
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateAll = () => {
    const errors = {};

    if (!orgEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgEmail)) {
      errors.email = 'Invalid email format';
    }

    ['facebook', 'instagram', 'twitter'].forEach(p => {
      const url = socialMedia[p];
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        errors[`social_${p}`] = 'URL must start with http:// or https://';
      }
    });

    locations.forEach((loc, i) => {
      if (!loc.locationName.trim()) {
        errors[`loc_${i}_locationName`] = 'Location name is required';
      }
      if (!loc.address.trim()) {
        errors[`loc_${i}_address`] = 'Address is required';
      }
      if (!loc.phone.trim()) {
        errors[`loc_${i}_phone`] = 'Phone number is required';
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateAll()) return;

    const hasUnnamedLocation = locations.some(l => !l.locationId);
    if (hasUnnamedLocation) {
      setSaveError('Please enter a name for every location before saving.');
      return;
    }

    setSaving(true);
    setSavedMessage('');
    setSaveError('');

    try {
      // Strip internal UI-only flags before persisting
      const cleanLocations = locations.map(({ _isNew, _key, ...loc }) => loc);

      const headOffice = cleanLocations.find(l => l.isHeadOffice) || cleanLocations[0];

      // Persist everything in one Firestore update
      const salonRef = doc(db, 'salons', salonId);
      await updateDoc(salonRef, {
        email:       orgEmail,
        socialMedia: socialMedia,
        // Keep root-level address/phone in sync with Head Office for backward compat
        address:     headOffice.address,
        phone:       headOffice.phone,
        locations:   cleanLocations,
        updatedAt:   serverTimestamp(),
      });

      // Cascade email to the clients collection record for this salon
      try {
        const clientsSnap = await getDocs(
          query(collection(db, 'clients'), where('salonId', '==', salonId))
        );
        for (const clientDoc of clientsSnap.docs) {
          await updateDoc(clientDoc.ref, {
            email:     orgEmail,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (clientErr) {
        console.error('Error cascading email to clients collection:', clientErr);
      }

      // Run migration if this is the first time locations are being saved,
      // or if a previous migration attempt didn't complete.
      if (needsMigration) {
        await runLocationMigration(salonId, headOffice);
        setNeedsMigration(false);
      }

      // Keep Admin.jsx local state in sync for the global config object
      onUpdate({
        email:      orgEmail,
        socialMedia: socialMedia,
        address:    headOffice.address,
        phone:      headOffice.phone,
      });

      // Commit the clean locations (no _isNew/_key) to local state, re-attaching stable _key
      setLocations(cleanLocations.map(loc => ({ ...loc, _key: loc.locationId })));

      setSavedMessage('Contact information saved successfully.');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      console.error('Error saving contact info:', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (!locationsLoaded) {
    return (
      <div className="contact-editor">
        <p className="contact-loading">Loading contact information…</p>
      </div>
    );
  }

  return (
    <div className="contact-editor">
      {/* ── Page header ── */}
      <div className="editor-page-header">
        <h2>Contact Information</h2>
        <div className="editor-page-actions">
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>
            Live Preview
          </button>
          <button
            className="page-action-btn page-action-save"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {savedMessage && <div className="success-message">{savedMessage}</div>}
      {saveError    && <div className="contact-error-banner">{saveError}</div>}

      {/* ── Organisation-level contact ── */}
      <section className="contact-section">
        <h3>Organisation Contact</h3>
        <p className="contact-section-hint">
          These details apply across all locations and appear in organisation-wide communications.
        </p>

        <div className="form-group">
          <label htmlFor="orgEmail">Email Address *</label>
          <input
            type="email"
            id="orgEmail"
            value={orgEmail}
            onChange={e => { setOrgEmail(e.target.value); clearFieldError('email'); }}
            className={fieldErrors.email ? 'field-invalid' : orgEmail ? 'field-valid' : ''}
            placeholder="salon@example.com"
          />
          {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
        </div>

        <h4 className="contact-subsection-title">Social Media</h4>
        <div className="social-media-fields">
          {['facebook', 'instagram', 'twitter'].map(platform => (
            <div className="form-group" key={platform}>
              <label htmlFor={`sm_${platform}`}>
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </label>
              <input
                type="url"
                id={`sm_${platform}`}
                value={socialMedia[platform]}
                onChange={e => {
                  setSocialMedia(p => ({ ...p, [platform]: e.target.value }));
                  clearFieldError(`social_${platform}`);
                }}
                className={fieldErrors[`social_${platform}`] ? 'field-invalid' : ''}
                placeholder={`https://${platform}.com/yoursalon`}
              />
              {fieldErrors[`social_${platform}`] && (
                <span className="error-message">{fieldErrors[`social_${platform}`]}</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Locations ── */}
      <section className="contact-section">
        <div className="locations-section-header">
          <div>
            <h3>Locations</h3>
            {needsMigration && (
              <p className="migration-notice">
                This is your first time setting up locations. Enter the name for your existing
                location below — all current records will be automatically assigned to it when
                you save.
              </p>
            )}
          </div>
          <button className="add-location-btn" type="button" onClick={addLocation}>
            + Add Location
          </button>
        </div>

        <div className="locations-list">
          {locations.map((loc, index) => (
            <div
              key={loc._key}
              className={`location-card${loc.isHeadOffice ? ' location-card--hq' : ''}`}
            >
              {/* Card header */}
              <div className="location-card-header">
                <div className="location-card-meta">
                  {loc.isHeadOffice ? (
                    <span className="hq-badge">Head Office</span>
                  ) : (
                    <button
                      className="set-hq-btn"
                      type="button"
                      onClick={() => setHeadOffice(index)}
                    >
                      Set as Head Office
                    </button>
                  )}
                  {loc.locationId && (
                    <span className="location-id-pill">ID: {loc.locationId}</span>
                  )}
                </div>

                <div className="location-card-actions">
                  {/* Only show Remove when there is more than one location and this is not HQ */}
                  {locations.length > 1 && !loc.isHeadOffice && (
                    removeConfirm === index ? (
                      <span className="remove-confirm-inline">
                        Remove this location?&nbsp;
                        <button className="confirm-yes-btn" type="button" onClick={confirmRemove}>Yes</button>
                        &nbsp;
                        <button className="confirm-no-btn" type="button" onClick={cancelRemove}>No</button>
                      </span>
                    ) : (
                      <button
                        className="remove-location-btn"
                        type="button"
                        onClick={() => requestRemove(index)}
                      >
                        Remove
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Card body */}
              <div className="location-card-body">
                <div className="form-group">
                  <label>Location Name *</label>
                  <input
                    type="text"
                    value={loc.locationName}
                    onChange={e => updateLocationField(index, 'locationName', e.target.value)}
                    className={
                      fieldErrors[`loc_${index}_locationName`]
                        ? 'field-invalid'
                        : loc.locationName
                        ? 'field-valid'
                        : ''
                    }
                    placeholder="e.g. Action Paragon"
                  />
                  {fieldErrors[`loc_${index}_locationName`] && (
                    <span className="error-message">{fieldErrors[`loc_${index}_locationName`]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Address *</label>
                  <textarea
                    value={loc.address}
                    onChange={e => updateLocationField(index, 'address', e.target.value)}
                    className={
                      fieldErrors[`loc_${index}_address`]
                        ? 'field-invalid'
                        : loc.address
                        ? 'field-valid'
                        : ''
                    }
                    placeholder="Enter the full address for this location"
                    rows={3}
                  />
                  {fieldErrors[`loc_${index}_address`] && (
                    <span className="error-message">{fieldErrors[`loc_${index}_address`]}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={loc.phone}
                    onChange={e => updateLocationField(index, 'phone', e.target.value)}
                    className={
                      fieldErrors[`loc_${index}_phone`]
                        ? 'field-invalid'
                        : loc.phone
                        ? 'field-valid'
                        : ''
                    }
                    placeholder="+65 6123 4567"
                  />
                  {fieldErrors[`loc_${index}_phone`] && (
                    <span className="error-message">{fieldErrors[`loc_${index}_phone`]}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default ContactInfoEditor;
